import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { getRequest } from "services/api";

const PriceByAssetGroupChart = () => {
  const [priceData, setPriceData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPriceByAssetGroup = async () => {
      try {
        setLoading(true);
        const response = await getRequest("/ahr/statistics/price-group");
        if (response.success) {
          setPriceData(response.data);
        }
      } catch (error) {
        console.error("Error fetching price by asset group:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPriceByAssetGroup();
  }, []);

  const chartOptions = {
    chart: {
      type: "pie",
      height: 300,
      toolbar: {
        show: false,
      },
      fontFamily: "Inter, sans-serif",
    },
    labels: priceData.map(item => {
      // Truncate long group names for better display
      const name = item.group_name;
      return name.length > 15 ? name.substring(0, 15) + "..." : name;
    }),
    colors: ["#206bc4", "#d63939", "#2fb344", "#f59e0b", "#667eea", "#e83e8c", "#17a2b8"], // Extended Tabler colors
    legend: {
      position: "right",
      fontFamily: "Inter, sans-serif",
      fontSize: "12px",
      labels: {
        colors: undefined,
        useSeriesColors: false
      },
      formatter: function(seriesName, opts) {
        // Show full name in legend
        const fullName = priceData[opts.seriesIndex]?.group_name || seriesName;
        return fullName.length > 20 ? fullName.substring(0, 20) + "..." : fullName;
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        // Show percentage for slices > 5%, otherwise show nothing to avoid clutter
        return val > 5 ? val.toFixed(1) + "%" : "";
      },
      style: {
        fontFamily: "Inter, sans-serif",
        fontSize: "11px",
        fontWeight: "bold",
      },
      dropShadow: {
        enabled: false,
      },
    },
    tooltip: {
      style: {
        fontFamily: "Inter, sans-serif",
        fontSize: "12px",
      },
      y: {
        formatter: function (value, opts) {
          const fullName = priceData[opts.seriesIndex]?.group_name || "";
          return `<div><strong>${fullName}</strong><br/>Rp ${new Intl.NumberFormat("id-ID").format(value)}</div>`;
        },
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '40%', // Create a donut chart for better readability
        },
        dataLabels: {
          offset: -10,
        },
      },
    },
    responsive: [{
      breakpoint: 768,
      options: {
        legend: {
          position: "bottom",
          fontSize: "11px",
        },
        chart: {
          height: 350,
        },
      }
    }, {
      breakpoint: 480,
      options: {
        legend: {
          position: "bottom",
          fontSize: "10px",
        },
        chart: {
          height: 300,
        },
      }
    }]
  };

  const chartSeries = priceData.map(item => parseInt(item.total_price));

  if (loading) {
    return (
      <div className="col-lg-6 mb-4">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Asset Group Prices</h3>
          </div>
          <div className="card-body">
            <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-lg-5 mb-4">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Asset Group Prices</h3>
        </div>
        <div className="card-body">
          {priceData.length > 0 ? (
            <>
              <Chart
                options={chartOptions}
                series={chartSeries}
                type="pie"
                height={300}
              />
              <div className="table-responsive mt-3">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Group Name</th>
                      <th className="text-end">Total Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {priceData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.group_name}</td>
                        <td className="text-end">
                          Rp {new Intl.NumberFormat("id-ID").format(item.total_price)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center text-muted">
              No asset group data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceByAssetGroupChart;
